import { EndpointBuilder } from "@reduxjs/toolkit/query";
import { rtkQueryAdapter } from "../rtkQueryAdapter";

export interface IApiError {
  message: string;
  status?: number;
  [key: string]: any;
}

export type EndpointConfig<Name extends string, P, R, E = IApiError, T = R> = {
  name: Name;
  type: "query" | "mutation";
  serviceFn: (payload: P) => Promise<R>;
  tagType?: string | readonly string[];
  argType?: "void"; // 👈 Add this
};

export function makeEndpoints<
  Configs extends readonly EndpointConfig<string, any, any, any>[]
>(
  builder: EndpointBuilder<any, any, any>,
  configs: Configs
): {
  [C in Configs[number] as C["name"]]: C["type"] extends "query"
    ? C extends EndpointConfig<any, infer P, infer R, infer E>
      ? ReturnType<typeof builder.query<R, P>>
      : never
    : C extends EndpointConfig<any, infer P, infer R, infer E>
    ? ReturnType<typeof builder.mutation<R, P>>
    : never;
} {
  const endpoints: any = {};

  configs.forEach(({ name, type, serviceFn, tagType, argType }) => {
    const tags = Array.isArray(tagType) ? tagType : tagType ? [tagType] : [];

    if (type === "query") {
      endpoints[name] = builder.query({
        queryFn:
          argType === "void"
            ? () => rtkQueryAdapter(() => serviceFn(undefined as any)) // 👈 No payload
            : (payload: any) => rtkQueryAdapter(() => serviceFn(payload)),
        providesTags: tags,
      });
    } else {
      endpoints[name] = builder.mutation({
        queryFn:
          argType === "void"
            ? () => rtkQueryAdapter(() => serviceFn(undefined as any))
            : (payload: any) => rtkQueryAdapter(() => serviceFn(payload)),
        invalidatesTags: tags,
      });
    }
  });

  return endpoints;
}
